import { useEffect, useState } from 'react';
import { MARKETPLACE_CHAINS } from '@shared/types/enums';
import { useConnectionStore } from '@ewf/stores/connection';
import { SunkenBox } from '@ewf/components/SunkenBox';
import { ProgressBadgePolkadot } from '@ewf/components/StatusBadge/ProgressBadge';
import { CloseButton } from '@ewf/components/ModalUI/CloseButton';

// Type for Polkadot.js extension account
interface InjectedAccount {
  address: string;
  name?: string;
  type?: string;
}

// Extend window to include injectedWeb3
declare global {
  interface Window {
    injectedWeb3?: {
      [key: string]: {
        enable: (origin: string) => Promise<{
          accounts: {
            get: () => Promise<InjectedAccount[]>;
          };
          signer: {
            signPayload?: (payload: any) => Promise<{ signature: string }>;
            signRaw?: (raw: { address: string; data: string; type: 'bytes' | 'payload' }) => Promise<{ signature: string }>;
          };
        }>;
      };
    };
  }
}

export const ConnectPolkadot = ({
  chain,
  onClose,
  onConnectionSuccess,
  onConnectionError,
}: Props) => {
  const onSessionConnected = useConnectionStore((state) => state.onSessionConnected);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const connectPolkadot = async () => {
      try {
        // Directly access only the Polkadot.js extension to prevent other extensions from opening
        const polkadotJs = window.injectedWeb3?.['polkadot-js'];

        if (!polkadotJs) {
          onConnectionError?.('Please install the Polkadot.js extension.');
          return;
        }

        // Enable the Polkadot.js extension - this opens the extension's popup
        // where user can select which accounts to share
        const injected = await polkadotJs.enable('EnergyWeb Marketplace');

        // Get the accounts user selected in the extension popup
        const accounts = await injected.accounts.get();

        if (accounts.length === 0) {
          onConnectionError?.('No accounts found in Polkadot.js extension.');
          return;
        }

        // Use the first account that user authorized
        const account = accounts[0];

        // Use the connection store to handle the session
        await onSessionConnected({
          type: 'polkadot-extension',
          address: account.address,
        });

        onConnectionSuccess?.();
      } catch (error) {
        console.error('Polkadot connect error:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to connect to Polkadot.js extension.';
        onConnectionError?.(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    connectPolkadot();
  }, [onConnectionSuccess, onConnectionError, onSessionConnected]);

  return (
    <div className="w-[550px] rounded-lg border-2 border-transparent p-8 gradient-border-primary-with-gray-90">
      <section className="flex w-full justify-end">
        <CloseButton onClick={onClose} />
      </section>
      <div className="mt-8 rounded-lg p-6 bg-popup-gradient">
        <SunkenBox className="mb-4 ml-auto text-center">
          <p className="text-lg font-bold">
            {loading ? 'Loading Polkadot.js extension' : 'Connecting...'}
          </p>
        </SunkenBox>
        <SunkenBox className="flex justify-center p-8">
          <ProgressBadgePolkadot />
        </SunkenBox>
      </div>
    </div>
  );
};

interface Props {
  chain: MARKETPLACE_CHAINS;
  onClose?: () => void;
  onConnectionSuccess: () => void;
  onConnectionError: (msg?: string) => void;
}
